import React, { useEffect } from "react";
import {
	Modal,
	Button,
	Row,
	Col,
	Container,
	Form,
	Spinner,
} from "react-bootstrap";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import { useAppSelector, useAppDispatch } from "../../../../store/hooks";
import {
	getAllBanksAsync,
	getAllDropdownLabourAsync,
	getAllDropdownStagesAsync,
} from "../../../../slices/settings/settingsSlice";
import { createLabourRequestAsync } from "../../../../slices/requests/requestsSlice";
import { RootState } from "../../../../store/store";
import { Select, FormControl, InputLabel, MenuItem } from "@mui/material";
import { toast } from "react-toastify";
import CurrencyInput from "../../../../components/form/currency-input";
import { handleAccountNumberChange } from "util/inputUtils";

interface CreateLabourRequestValues {
	company_id: string;
	head_name: string;
	stage_id: string;
	labour_type_id: string;
	bank_id: string;
	account_number: string;
	amount: string;
}
interface CreateLabourRequestProps {
	closeModal: () => void;
	show: boolean;
	onCreateSuccess: () => void;
	projectId: string | undefined | null;
}

const CreateLabourRequest: React.FC<CreateLabourRequestProps> = ({
	closeModal,
	show,
	onCreateSuccess,
	projectId,
}) => {
	const dispatch = useAppDispatch();
	const banks = useAppSelector((state) => state.settings.banks);
	const labourTypes = useAppSelector(
		(state) => state.settings.labourTypes?.data,
	);
	const stages = useAppSelector((state) => state.settings.stages?.data);
	const companyId = useAppSelector(
		(state: RootState) => state.auth.user?.company_id || "",
	);

	useEffect(() => {
		dispatch(getAllBanksAsync());
	}, [dispatch]);

	useEffect(() => {
		if (companyId) {
			dispatch(getAllDropdownLabourAsync({ companyId }));
		}
	}, [companyId, dispatch]);

	useEffect(() => {
		if (companyId) {
			dispatch(getAllDropdownStagesAsync({ companyId }));
		}
	}, [companyId, dispatch]);

	console.log("Banks", banks);
	console.log("labourTypes", labourTypes);
	console.log("stages", stages);
	console.log("projectId", projectId);

	return (
		<Container fluid className="">
			<Modal
				centered
				size="lg"
				show={show}
				onHide={closeModal}
				style={{ maxHeight: "100vh", overflowY: "auto" }}
			>
				<div className="px-5">
					<Formik
						initialValues={{
							company_id: companyId || "",
							head_name: "",
							stage_id: "",
							labour_type_id: "",
							bank_id: "",
							account_number: "",
							amount: "",
							projectId: projectId || "",
						}}
						validationSchema={Yup.object().shape({
							company_id: Yup.string().required("Required"),
							head_name: Yup.string().required("Required"),
							stage_id: Yup.string().required("Required"),
							labour_type_id: Yup.string().required("Required"),
							bank_id: Yup.string().required("Required"),
							account_number: Yup.string().required("10 digits Required").min(10).max(10),
							amount: Yup.string().required("Required"),
							projectId: Yup.string().required("Required"),
						})}
						onSubmit={(
							values: CreateLabourRequestValues,
							{ setSubmitting },
						) => {
							console.log(values);
							const plainAmount = parseFloat(
								values.amount.replace(/[^0-9.]/g, ""),
							);
							const roundedAmount = Math.round(plainAmount);
							const addSpacesToMessage = (message: any) => {
								return message.replace(/([a-z])([A-Z])/g, '$1 $2');
							};
							dispatch(
								createLabourRequestAsync({
									...values,
									amount: roundedAmount.toString(),
									company_id: companyId || "",
									projectId: projectId || "",
								}),
							)
								.unwrap()
								.then((response) => {
									toast.success("Labour request created successfully");
									onCreateSuccess();
									closeModal();
								})
								.catch((error) => {
									const errorMessage = error.message ? addSpacesToMessage(error.message) : "An unexpected error occurred";
									toast.error(errorMessage);
								})
								.finally(() => {
									setSubmitting(false);
								});
						}}
					>
						{({
							errors,
							touched,
							handleSubmit,
							isSubmitting,
							values,
							handleChange,
							handleBlur,
						}) => (
							<Form onSubmit={handleSubmit}>
								<Modal.Header closeButton className="text-start mt-3">
									<h4 className="confirm-header">Create labour request</h4>
								</Modal.Header>
								<Modal.Body>
									<Row>
										<Col className="mt-2">
											<label className="mt-2">
												<p>
													Head name <span className="text-danger">*</span>
												</p>
											</label>
											<Field
												type="text"
												name="head_name"
												className={`fluent-input  ${touched.head_name && errors.head_name
													? "is-invalid"
													: ""
													}`}
												required
											/>
										</Col>
										<Col className="mt-2">
											<label className="mt-2">
												<p>
													Stage <span className="text-danger">*</span>
												</p>
											</label>
											<FormControl fullWidth>
												<InputLabel>Select stage</InputLabel>
												<Select
													label="Select stage"
													name="stage_id"
													value={values.stage_id}
													onChange={handleChange}
												>
													{stages?.map((stage) => (
														<MenuItem key={stage.id} value={stage.id}>
															{stage.stage}
														</MenuItem>
													))}
												</Select>
											</FormControl>
											<div className="invalid-feedback d-block">
												{touched.stage_id && errors.stage_id}
											</div>
										</Col>
									</Row>
									<Row>
										<Col className="mt-2">
											<label className="mt-2">
												<p>
													Labour type <span className="text-danger">*</span>
												</p>
											</label>
											<FormControl fullWidth>
												<InputLabel>Select labour type</InputLabel>
												<Select
													label="Select labour type"
													name="labour_type_id"
													value={values.labour_type_id}
													onChange={handleChange}
												>
													{labourTypes?.map((labourType) => (
														<MenuItem key={labourType.id} value={labourType.id}>
															{labourType.labour_type}
														</MenuItem>
													))}
												</Select>
											</FormControl>
											<div className="invalid-feedback d-block">
												{touched.labour_type_id && errors.labour_type_id}
											</div>
										</Col>
										<Col className="mt-2">
											<label className="mt-2">
												<p>
													Amount <span className="text-danger">*</span>
												</p>
											</label>
											<CurrencyInput
												name="amount"
												placeholder="Enter amount"
												className={`fluent-input  ${touched.amount && errors.amount ? "is-invalid" : ""
													}`}
												value={values.amount}
												onChange={handleChange}
												onBlur={handleBlur}
											/>
										</Col>
									</Row>
									<Row>
										<Col className="mt-2">
											<label className="mt-2">
												<p>
													Bank <span className="text-danger">*</span>
												</p>
											</label>
											<FormControl fullWidth>
												<InputLabel>Select bank </InputLabel>
												<Select
													label="Select bank "
													name="bank_id"
													value={values.bank_id}
													onChange={handleChange}
												>
													{Array.isArray(banks) && banks.length > 0 ? (
														banks.map((bank) => (
															<MenuItem key={bank.id} value={bank.id}>
																{bank.bank_name}
															</MenuItem>
														))
													) : (
														<p> no banks </p>
													)}
												</Select>
											</FormControl>
											<div className="invalid-feedback d-block">
												{touched.bank_id && errors.bank_id}
											</div>
										</Col>
										<Col className="mt-2">
											<label className="mt-2">
												<p>
													Account number <span className="text-danger">*</span>
												</p>
											</label>
											<Field
												type="text"
												name="account_number"
												className={`fluent-input  ${touched.account_number && errors.account_number
													? "is-invalid"
													: ""
													}`}
												required
												onInput={handleAccountNumberChange}
											/>
										</Col>
									</Row>

									<Modal.Footer className="mt-5">
										<Row>
											<Col md={6}>
												<div className=" mt-1">
													<Button onClick={closeModal} className="cancel-btn">
														<span className="text-center">Cancel </span>{" "}
													</Button>
												</div>
											</Col>
											<Col md={6}>
												<div className=" mt-1">
													<Button
														type="submit"
														className=" border-0 create-btn bg-color-1"
													>
														{" "}
														{isSubmitting ? (
															<Spinner animation="border" size="sm" />
														) : (
															<span className="text-center">
																Create request
															</span>
														)}
													</Button>
												</div>
											</Col>
										</Row>
									</Modal.Footer>
								</Modal.Body>
							</Form>
						)}
					</Formik>
				</div>
			</Modal>
		</Container>
	);
};

export default CreateLabourRequest;
